import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { BackButton } from '../../components/ui/Button';
import { PageHeader, TipBox } from '../../components/ui';
import { Icon } from '@iconify/react';

// Whisper GPU API 配置
const WHISPER_API_URL = 'https://whisper-gpu-450343397052.us-central1.run.app';
const FFMPEG_API_URL = 'https://ffmpeg-gpu-450343397052.us-central1.run.app';

function SubtitleGenerator() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // State
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [rawSegments, setRawSegments] = useState([]); // Whisper 原始段落
  const [currentTime, setCurrentTime] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState({ message: '', progress: 0 });
  const [editingIndex, setEditingIndex] = useState(null);

  // 字幕樣式設定
  const [subtitleStyle, setSubtitleStyle] = useState({
    fontSize: 48,
    fontColor: '#FFFFFF',
    outlineColor: '#000000',
    outlineWidth: 2,
    shadow: true,
    position: 'bottom' // bottom, top, center
  });

  // 處理影片上傳
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      setRawSegments([]);
    }
  };

  // 影片載入完成
  const handleVideoLoaded = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
    }
  };

  // 更新當前播放時間
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // 使用 Whisper GPU 辨識字幕（精確時間戳）
  const transcribeWithWhisper = async () => {
    if (!videoFile) return;

    setIsTranscribing(true);
    setProgress({ message: '正在上傳影片到 Whisper GPU...', progress: 10 });

    try {
      const formData = new FormData();
      formData.append('file', videoFile);
      formData.append('language', 'zh');

      setProgress({ message: '正在進行 GPU 加速語音辨識...', progress: 30 });

      const response = await fetch(`${WHISPER_API_URL}/transcribe`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Whisper 轉錄失敗: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error('語音轉文字處理失敗');
      }

      setProgress({ message: '正在處理字幕段落...', progress: 70 });

      // 儲存 Whisper 原始段落（保留精確時間戳）
      setRawSegments(result.segments);

      setProgress({ message: `✅ 完成！辨識 ${result.segments.length} 個段落`, progress: 100 });

      setTimeout(() => {
        setIsTranscribing(false);
        setProgress({ message: '', progress: 0 });
      }, 2000);

    } catch (error) {
      console.error('Whisper 辨識錯誤:', error);
      setProgress({ message: `❌ 錯誤: ${error.message}`, progress: 0 });
      setTimeout(() => {
        setIsTranscribing(false);
        setProgress({ message: '', progress: 0 });
      }, 3000);
    }
  };

  // 編輯字幕文字
  const handleEditSubtitle = (index, newText) => {
    const updated = [...rawSegments];
    updated[index].text = newText;
    setRawSegments(updated);
  };

  // 刪除字幕段落
  const handleDeleteSubtitle = (index) => {
    const updated = rawSegments.filter((_, i) => i !== index);
    setRawSegments(updated);
  };

  // 分割字幕段落
  const handleSplitSubtitle = (index, splitPoint) => {
    const segment = rawSegments[index];
    const text1 = segment.text.slice(0, splitPoint).trim();
    const text2 = segment.text.slice(splitPoint).trim();

    if (!text1 || !text2) return;

    const duration = segment.end - segment.start;
    const ratio = splitPoint / segment.text.length;
    const splitTime = segment.start + (duration * ratio);

    const updated = [...rawSegments];
    updated.splice(index, 1,
      { start: segment.start, end: splitTime, text: text1 },
      { start: splitTime, end: segment.end, text: text2 }
    );
    setRawSegments(updated);
  };

  // 合併字幕段落
  const handleMergeSubtitle = (index) => {
    if (index >= rawSegments.length - 1) return;

    const current = rawSegments[index];
    const next = rawSegments[index + 1];

    const merged = {
      start: current.start,
      end: next.end,
      text: `${current.text} ${next.text}`
    };

    const updated = [...rawSegments];
    updated.splice(index, 2, merged);
    setRawSegments(updated);
  };

  // 跳轉到字幕時間點
  const seekToSubtitle = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play();
    }
  };

  // 匯出帶字幕的影片（FFmpeg 處理樣式）
  const exportVideoWithSubtitles = async () => {
    if (!videoFile || rawSegments.length === 0) return;

    setIsExporting(true);
    setProgress({ message: '正在上傳影片到 FFmpeg GPU...', progress: 10 });

    try {
      // 準備 SRT 格式字幕
      const srtContent = generateSRT(rawSegments);

      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('subtitles', srtContent);

      // 傳遞樣式參數
      formData.append('fontSize', subtitleStyle.fontSize.toString());
      formData.append('fontColor', subtitleStyle.fontColor);
      formData.append('outlineColor', subtitleStyle.outlineColor);
      formData.append('outlineWidth', subtitleStyle.outlineWidth.toString());
      formData.append('shadow', subtitleStyle.shadow ? '1' : '0');
      formData.append('position', subtitleStyle.position);

      setProgress({ message: '正在燒錄字幕 (GPU 加速)...', progress: 30 });

      const response = await fetch(`${FFMPEG_API_URL}/add-subtitles`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`FFmpeg 處理失敗: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error('影片處理失敗');
      }

      setProgress({ message: '正在下載影片...', progress: 80 });

      // 下載處理後的影片
      const downloadResponse = await fetch(`${FFMPEG_API_URL}${result.downloadUrl}`);
      const videoBlob = await downloadResponse.blob();

      // 觸發下載
      const url = URL.createObjectURL(videoBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subtitled_${videoFile.name.replace(/\.[^/.]+$/, '')}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setProgress({ message: '✅ 匯出完成！', progress: 100 });

      setTimeout(() => {
        setIsExporting(false);
        setProgress({ message: '', progress: 0 });
      }, 2000);

    } catch (error) {
      console.error('匯出錯誤:', error);
      setProgress({ message: `❌ 錯誤: ${error.message}`, progress: 0 });
      setTimeout(() => {
        setIsExporting(false);
        setProgress({ message: '', progress: 0 });
      }, 3000);
    }
  };

  // 生成 SRT 格式字幕
  const generateSRT = (segments) => {
    return segments.map((seg, index) => {
      const start = formatSRTTime(seg.start);
      const end = formatSRTTime(seg.end);
      return `${index + 1}\n${start} --> ${end}\n${seg.text}\n`;
    }).join('\n');
  };

  // 格式化 SRT 時間格式
  const formatSRTTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
  };

  // 獲取當前顯示的字幕
  const getCurrentSubtitle = () => {
    return rawSegments.find(seg =>
      currentTime >= seg.start && currentTime <= seg.end
    );
  };

  // 格式化時間顯示
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen">
        <Sidebar activePage="home" />

        <div className="flex-1 overflow-y-auto">
          <BackButton
            onClick={() => navigate('/home')}
            className="fixed top-6 left-[280px] z-[100]"
          >
            返回首頁
          </BackButton>

          <div className="max-w-[1400px] mx-auto px-8 py-16">
            <PageHeader
              badge="影片工具"
              badgeIcon="mdi:video-box"
              title="AI 字幕生成器"
              subtitle="使用 Whisper GPU 精確辨識字幕時間軸"
            />

            {/* 上傳區域 */}
            {!videoFile && (
              <div className="mt-8">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-purple-500 hover:bg-purple-50 transition-all cursor-pointer"
                >
                  <Icon icon="mdi:cloud-upload" className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    點擊上傳影片
                  </h3>
                  <p className="text-sm text-gray-500">
                    支援 MP4, MOV, AVI 等格式
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <TipBox className="mt-6">
                  <div className="space-y-2 text-sm">
                    <p><strong>工作流程：</strong></p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li><strong>Whisper 辨識</strong> - 獲得精確時間戳（0.01秒精度）</li>
                      <li><strong>編輯字幕</strong> - 修改文字、分割/合併段落</li>
                      <li><strong>FFmpeg 燒錄</strong> - 匯出時加上樣式（字體、顏色、位置）</li>
                    </ol>
                  </div>
                </TipBox>
              </div>
            )}

            {/* 影片預覽和編輯區 */}
            {videoFile && (
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 左側：影片預覽 */}
                <div className="space-y-4">
                  <div className="bg-white rounded-xl shadow-sm p-4">
                    <div className="relative bg-black rounded-lg overflow-hidden">
                      <video
                        ref={videoRef}
                        src={videoUrl}
                        onLoadedMetadata={handleVideoLoaded}
                        onTimeUpdate={handleTimeUpdate}
                        controls
                        className="w-full"
                      />

                      {/* 字幕預覽疊加 */}
                      {getCurrentSubtitle() && (
                        <div className={`absolute left-0 right-0 text-center ${
                          subtitleStyle.position === 'bottom' ? 'bottom-16' :
                          subtitleStyle.position === 'top' ? 'top-16' :
                          'top-1/2 -translate-y-1/2'
                        }`}>
                          <div
                            className="inline-block px-4 py-2 rounded-lg font-semibold"
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              color: subtitleStyle.fontColor,
                              fontSize: `${subtitleStyle.fontSize * 0.5}px`,
                              textShadow: subtitleStyle.shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
                              WebkitTextStroke: `${subtitleStyle.outlineWidth * 0.5}px ${subtitleStyle.outlineColor}`
                            }}
                          >
                            {getCurrentSubtitle().text}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(videoDuration)}</span>
                    </div>
                  </div>

                  {/* 操作按鈕 */}
                  <div className="flex gap-3">
                    <button
                      onClick={transcribeWithWhisper}
                      disabled={isTranscribing}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isTranscribing ? (
                        <>
                          <Icon icon="mdi:loading" className="animate-spin" />
                          辨識中...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:microphone" />
                          Whisper 辨識
                        </>
                      )}
                    </button>

                    {rawSegments.length > 0 && (
                      <button
                        onClick={exportVideoWithSubtitles}
                        disabled={isExporting}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isExporting ? (
                          <>
                            <Icon icon="mdi:loading" className="animate-spin" />
                            匯出中...
                          </>
                        ) : (
                          <>
                            <Icon icon="mdi:download" />
                            燒錄匯出
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* 進度顯示 */}
                  {progress.message && (
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-center gap-3">
                        <Icon icon="mdi:loading" className="text-blue-500 w-5 h-5 animate-spin" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{progress.message}</p>
                          {progress.progress > 0 && progress.progress < 100 && (
                            <div className="mt-2 bg-white rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-blue-500 h-full transition-all duration-300"
                                style={{ width: `${progress.progress}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 字幕樣式設定 */}
                  {rawSegments.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold mb-4">字幕樣式</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">字體大小</label>
                          <input
                            type="range"
                            min="24"
                            max="72"
                            value={subtitleStyle.fontSize}
                            onChange={(e) => setSubtitleStyle({...subtitleStyle, fontSize: parseInt(e.target.value)})}
                            className="w-full"
                          />
                          <span className="text-sm text-gray-600">{subtitleStyle.fontSize}px</span>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">字體顏色</label>
                            <input
                              type="color"
                              value={subtitleStyle.fontColor}
                              onChange={(e) => setSubtitleStyle({...subtitleStyle, fontColor: e.target.value})}
                              className="w-full h-10 rounded cursor-pointer"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">外框顏色</label>
                            <input
                              type="color"
                              value={subtitleStyle.outlineColor}
                              onChange={(e) => setSubtitleStyle({...subtitleStyle, outlineColor: e.target.value})}
                              className="w-full h-10 rounded cursor-pointer"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">位置</label>
                          <select
                            value={subtitleStyle.position}
                            onChange={(e) => setSubtitleStyle({...subtitleStyle, position: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          >
                            <option value="bottom">底部</option>
                            <option value="top">頂部</option>
                            <option value="center">中間</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 右側：字幕列表 */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      字幕段落 ({rawSegments.length})
                    </h3>
                    {rawSegments.length > 0 && (
                      <button
                        onClick={() => setRawSegments([])}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        清空全部
                      </button>
                    )}
                  </div>

                  {rawSegments.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <Icon icon="mdi:subtitles-outline" className="w-12 h-12 mx-auto mb-2" />
                      <p>尚未辨識字幕</p>
                      <p className="text-xs mt-1">點擊「Whisper 辨識」開始</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[700px] overflow-y-auto">
                      {rawSegments.map((seg, index) => {
                        const isActive = currentTime >= seg.start && currentTime <= seg.end;
                        const duration = seg.end - seg.start;

                        return (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              isActive
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-600">
                                {index + 1}
                              </div>

                              <div className="flex-1 min-w-0">
                                {/* 時間 */}
                                <div className="flex items-center gap-2 mb-2">
                                  <button
                                    onClick={() => seekToSubtitle(seg.start)}
                                    className="text-xs text-purple-600 hover:text-purple-700 font-mono"
                                  >
                                    {formatTime(seg.start)} → {formatTime(seg.end)}
                                  </button>
                                  <span className="text-xs text-gray-400">
                                    ({duration.toFixed(2)}s)
                                  </span>
                                </div>

                                {/* 字幕文字 */}
                                {editingIndex === index ? (
                                  <textarea
                                    value={seg.text}
                                    onChange={(e) => handleEditSubtitle(index, e.target.value)}
                                    onBlur={() => setEditingIndex(null)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm min-h-[60px]"
                                    autoFocus
                                  />
                                ) : (
                                  <p
                                    onClick={() => setEditingIndex(index)}
                                    className="text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded whitespace-pre-wrap"
                                  >
                                    {seg.text}
                                  </p>
                                )}

                                {/* 操作按鈕 */}
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => handleMergeSubtitle(index)}
                                    disabled={index >= rawSegments.length - 1}
                                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    與下段合併
                                  </button>
                                  <button
                                    onClick={() => {
                                      const splitPoint = Math.floor(seg.text.length / 2);
                                      handleSplitSubtitle(index, splitPoint);
                                    }}
                                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  >
                                    分割段落
                                  </button>
                                </div>
                              </div>

                              <button
                                onClick={() => handleDeleteSubtitle(index)}
                                className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <Icon icon="mdi:close" className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubtitleGenerator;
